/**
 * Koordinate mreže graditelja (docs/ARHITEKTURA.md, 6.3 – Graditelj).
 *
 * Strežnik vrne vrstice brez koordinat; vmesnik ob odprtju vsaki izbirni
 * različici (tudi skriti podvrstici) dodeli zaporedno številko. Razpiranje
 * in zapiranje vrstic številk ne spremeni, zato formula med urejanjem ostane
 * ista. V bazo gre samo pomen celice (vir, agregacija, časovni kontekst).
 * Naslovi in vrstice, ki so posebnost podjetja, številke nimajo – niso
 * izbirne.
 */
import type { AgregacijaClena, MrezaGraditelja, SklicFormule, VariantaGraditelja, VirFormule, VrsticaGraditelja } from '@koda/shared';

/** Vrstica mreže s številkami svojih različic (prva je glavna vrstica). */
export interface OstevilcenaVrstica {
  /** Ključ vrstice v seji (indeks v odgovoru strežnika). */
  kljuc: string;
  vrstica: VrsticaGraditelja;
  /** Številka vsake različice; prazno pri naslovu in posebnosti podjetja. */
  stevilke: number[];
}

export interface Stevilcenje {
  vrstice: OstevilcenaVrstica[];
  /** Številka → vrstica in njena različica. */
  poStevilki: Map<number, { vrstica: OstevilcenaVrstica; varianta: VariantaGraditelja }>;
}

/** Oštevilči izbirne različice v vrstnem redu mreže. */
export function ostevilci(mreza: MrezaGraditelja): Stevilcenje {
  let st = 0;
  const poStevilki: Stevilcenje['poStevilki'] = new Map();
  const vrstice = mreza.vrstice.map((vrstica, i): OstevilcenaVrstica => {
    const o: OstevilcenaVrstica = { kljuc: String(i), vrstica, stevilke: [] };
    if (vrstica.vir === null || vrstica.posebnost === true || vrstica.naslov === true) return o;
    for (const varianta of vrstica.variante) {
      st += 1;
      o.stevilke.push(st);
      poStevilki.set(st, { vrstica: o, varianta });
    }
    return o;
  });
  return { vrstice, poStevilki };
}

/** Vir in agregacija celice s številko (brez časovnega konteksta, ki ga da stolpec). */
export function sklicStevilke(s: Stevilcenje, st: number): Omit<SklicFormule, 'period'> | null {
  const najdeno = s.poStevilki.get(st);
  if (najdeno === undefined || najdeno.vrstica.vrstica.vir === null) return null;
  return { source: najdeno.vrstica.vrstica.vir, aggregation: najdeno.varianta.aggregation };
}

/** Isti vir: primerjava po poljih (vrstni red ključev v zapisu ni pomemben). */
function enakVir(a: VirFormule, b: VirFormule): boolean {
  if (a.type === 'report_line' && b.type === 'report_line') return a.level === b.level && a.reference_key === b.reference_key;
  if (a.type === 'semantic_role' && b.type === 'semantic_role') return a.role === b.role;
  if (a.type === 'derived' && b.type === 'derived') return a.key === b.key;
  return false;
}

/** Številka celice za vir in agregacijo (obratno od `sklicStevilke`); `null`, če je v mreži ni. */
export function stevilkaSklica(s: Stevilcenje, sklic: { source: VirFormule; aggregation: AgregacijaClena }): number | null {
  for (const [st, { vrstica, varianta }] of s.poStevilki) {
    if (vrstica.vrstica.vir !== null && enakVir(vrstica.vrstica.vir, sklic.source) && varianta.aggregation === sklic.aggregation) {
      return st;
    }
  }
  return null;
}

/** Koordinata celice v besedilu formule (ne del imena funkcije ali števila). */
const KOORDINATA = /(?<![A-Za-z0-9_.,])([A-Ca-c])(\d{1,4})(?![\d.,])/g;

/**
 * Besedilo formule, napisano za eno številčenje, prevede na drugo (npr. ob
 * menjavi podjetja za predogled): vsaka znana koordinata se prek pomena
 * (vir in različica) preslika v številko iste vrstice v novi mreži, stolpec
 * ostane. Tako formula ob menjavi podjetja nikoli tiho ne spremeni pomena –
 * tudi nepopolna formula (»=A5/«). Koordinata, ki je v stari mreži ni bilo,
 * ostane, kot je (ni imela pomena). `null`: vrstice, ki jo formula
 * uporablja, v novi mreži ni – formule ni mogoče prenesti.
 */
export function prevediKoordinate(besedilo: string, stara: Stevilcenje, nova: Stevilcenje): string | null {
  let neprevedljiva = false;
  const prevod = besedilo.replace(KOORDINATA, (celota, stolpec: string, st: string) => {
    const sklic = sklicStevilke(stara, Number(st));
    if (sklic === null) return celota;
    const novaSt = stevilkaSklica(nova, sklic);
    if (novaSt === null) {
      neprevedljiva = true;
      return celota;
    }
    return `${stolpec}${String(novaSt)}`;
  });
  return neprevedljiva ? null : prevod;
}
